import { getAffectationColumns } from '@/components/Affectations/affectationColumns';
import DecisionActionIcon from '@/components/Affectations/DecisionActionIcon';
import useAffectationDecision, {
  type Decision,
} from '@/components/Affectations/useAffectationDecision';
import { useGetAffectationsQuery } from '@/features/api/endpoints/affectationEndpoints';
import type { Affectation } from '@/models/types';
import { Group } from '@mantine/core';
import { useTranslate } from '@tolgee/react';
import { DataTable } from 'mantine-datatable';

type Props = {
  userId: string;
  status: Affectation['status'];
};

/** Décision permettant de corriger une affectation selon son statut actuel. */
const CORRECTION_DECISION: Partial<Record<Affectation['status'], Decision>> = {
  published: 'reject',
  archived: 'approve',
};

/**
 * Affectations d'un utilisateur pour un statut donné, avec la possibilité de changer
 * leur statut (refuser une affectation active, approuver une affectation refusée).
 * À rendre uniquement quand l'onglet est actif : la requête n'est faite qu'à l'ouverture.
 */
const UserAffectationsByStatus = ({ userId, status }: Props) => {
  const { t } = useTranslate();
  const { onDecisionClick, isUpdating } = useAffectationDecision(userId);
  const decision = CORRECTION_DECISION[status];

  const { data: affectations = [], isFetching } = useGetAffectationsQuery({
    fields: ['*', { role: ['name'], etablissement: ['name'] }],
    filter: {
      user: { _eq: userId },
      status: { _eq: status },
    },
    sort: ['-date_created'],
    limit: -1,
  });

  return (
    <DataTable
      striped
      highlightOnHover
      withTableBorder
      minHeight={120}
      fetching={isFetching}
      records={affectations}
      idAccessor="id"
      noRecordsText={t('profil.affectations.empty')}
      columns={[
        ...getAffectationColumns(t, { withDateUpdated: status !== 'draft' }),
        ...(decision
          ? [
              {
                accessor: 'actions',
                title: t('profil.affectations.columns.actions'),
                textAlign: 'right' as const,
                render: (affectation: Affectation) => (
                  <Group gap="xs" justify="flex-end" wrap="nowrap">
                    <DecisionActionIcon
                      decision={decision}
                      disabled={isUpdating(affectation)}
                      onClick={() => onDecisionClick([affectation], decision, 'one')}
                    />
                  </Group>
                ),
              },
            ]
          : []),
      ]}
    />
  );
};

export default UserAffectationsByStatus;
